import { supabase } from "@/integrations/db/client";
import type { TablesInsert } from "@/integrations/db/types.generated";
import { toast } from "sonner";
import type { QueryClient } from "@tanstack/react-query";
import type { Comment } from "@/components/comments/CommentItem";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface UseCommentOperationsOptions {
  backlogItemId: string;
  comments: Comment[];
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  profiles: Profile[];
  queryClient: QueryClient;
  loadComments: () => Promise<void>;
}

/** Extract mentioned user IDs from comment text (supports both legacy and HTML formats) */
function extractMentionIds(text: string): string[] {
  const ids: string[] = [];

  // Legacy format: @[Display Name](user_id)
  const legacyRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let match;
  while ((match = legacyRegex.exec(text)) !== null) {
    if (match[2]) ids.push(match[2]);
  }

  // HTML format: <span data-type="mention" data-id="user_id">
  const htmlRegex = /data-type="mention"[^>]*data-id="([^"]+)"/g;
  while ((match = htmlRegex.exec(text)) !== null) {
    if (match[1]) ids.push(match[1]);
  }

  // Also check reverse attribute order
  const htmlRegex2 = /data-id="([^"]+)"[^>]*data-type="mention"/g;
  while ((match = htmlRegex2.exec(text)) !== null) {
    if (match[1]) ids.push(match[1]);
  }

  return [...new Set(ids)];
}

/**
 * The one write path for a new comment: the row, plus the mention records
 * denormalised out of its body and the caches those feed.
 *
 * It lives outside the hook so that a composer with no comment list of its own
 * — the backlog's quick-comment popover — can post a comment that is identical
 * in every respect to one written on the ticket page, rather than a second
 * insert with its own idea of the shape.
 */
export const createComment = async ({
  backlogItemId,
  commentText,
  parentCommentId,
  queryClient,
}: {
  backlogItemId: string;
  /** Stored content: TipTap HTML (see `plainTextToHTML` for a plain source). */
  commentText: string;
  parentCommentId?: string | null;
  queryClient: QueryClient;
}) => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const insertPayload: TablesInsert<"comments"> = {
    backlog_item_id: backlogItemId,
    user_id: user.id,
    comment_text: commentText,
  };
  if (parentCommentId) {
    insertPayload.parent_comment_id = parentCommentId;
  }

  const { data, error } = await supabase.from("comments").insert(insertPayload).select().single();
  if (error) throw error;

  if (data) {
    const mentions = extractMentionIds(commentText);
    if (mentions.length > 0) {
      await supabase.from("comment_mentions").insert(
        mentions.map((userId) => ({
          comment_id: data.id,
          mentioned_user_id: userId,
        })),
      );
      queryClient.invalidateQueries({ queryKey: ["mentions"] });
      queryClient.invalidateQueries({ queryKey: ["unread-mentions-count"] });
    }
  }

  return { data, userId: user.id };
};

export const useCommentOperations = ({
  backlogItemId,
  comments,
  setComments,
  profiles,
  queryClient,
  loadComments,
}: UseCommentOperationsOptions) => {
  const addComment = async (
    commentText: string,
    setNewComment: (v: string) => void,
    parentCommentId?: string | null,
  ) => {
    if (!commentText.trim()) return;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const userProfile = profiles.find((p) => p.id === user.id);

      const tempId = `temp-${Date.now()}`;
      const optimisticComment: Comment = {
        id: tempId,
        user_id: user.id,
        comment_text: commentText.trim(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        parent_comment_id: parentCommentId || null,
        user: userProfile || {
          id: user.id,
          first_name: null,
          last_name: null,
          email: user.email || "",
        },
      };

      setComments((prev) => [...prev, optimisticComment]);
      const trimmed = commentText.trim();
      setNewComment("");

      const { data } = await createComment({
        backlogItemId,
        commentText: trimmed,
        parentCommentId,
        queryClient,
      });

      if (data) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === tempId
              ? {
                  ...data,
                  user: optimisticComment.user,
                }
              : c,
          ),
        );
      }

      toast.success("Comment added");
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast.error("Failed to add comment");
      loadComments();
    }
  };

  const editComment = async (
    commentId: string,
    editText: string,
    setEditingId: (v: string | null) => void,
    setEditText: (v: string) => void,
  ) => {
    if (!editText.trim()) return;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const oldComment = comments.find((c) => c.id === commentId);
      const oldText = oldComment?.comment_text || "";

      const newUpdatedAt = new Date().toISOString();
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                comment_text: editText.trim(),
                updated_at: newUpdatedAt,
              }
            : c,
        ),
      );

      setEditingId(null);
      setEditText("");

      const { error } = await supabase
        .from("comments")
        .update({
          comment_text: editText.trim(),
          updated_at: newUpdatedAt,
        })
        .eq("id", commentId);

      if (error) throw error;

      await supabase.from("activity_logs").insert({
        user_id: user.id,
        action_type: "edited_comment",
        old_value: oldText,
        new_value: editText.trim(),
      });

      toast.success("Comment updated");
    } catch (error) {
      console.error("Failed to update comment:", error);
      toast.error("Failed to update comment");
      loadComments();
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const comment = comments.find((c) => c.id === commentId);

      setComments((prev) => prev.filter((c) => c.id !== commentId));

      const { error: deleteError } = await supabase.rpc("hide_comment", {
        p_comment_id: commentId,
      });

      if (deleteError) {
        console.error("Delete error:", deleteError);
        throw deleteError;
      }

      try {
        await supabase.from("activity_logs").insert({
          user_id: user.id,
          backlog_item_id: backlogItemId,
          action_type: "deleted_comment",
          old_value: comment?.comment_text || "",
        });
      } catch (logError) {
        console.error("Activity log error:", logError);
      }

      toast.success("Comment deleted");
    } catch (error) {
      console.error("Failed to delete comment:", error);
      toast.error("Failed to delete comment");
      loadComments();
    }
  };

  return { addComment, editComment, deleteComment };
};
